import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import {
    Sparkles, Send, ThumbsUp, ThumbsDown, User as UserIcon,
    Info, Loader2, ArrowRight, Trash2, ChevronRight, AlertTriangle,
} from "lucide-react";

const SUGGESTED = [
    "What needs my attention this week?",
    "Show payments due this month.",
    "Which subscriptions renew soon?",
    "Which documents expire in the next 90 days?",
    "Summarise this month.",
];

function Message({ role, text, sources, suggestions, onSuggest, onFeedback, kind }) {
    const isUser = role === "user";
    return (
        <div className={`flex gap-3 mb-5 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${role}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${isUser ? "bg-stone-900 text-white" : "bg-amber-100 text-amber-800"}`}>
                {isUser ? <UserIcon size={13} /> : <Sparkles size={13} />}
            </div>
            <div className={`flex-1 min-w-0 ${isUser ? "text-right" : ""}`}>
                <div className={`inline-block max-w-full text-left rounded-2xl px-4 py-3 whitespace-pre-line text-[13.5px] leading-relaxed ${
                    isUser
                        ? "bg-stone-900 text-white"
                        : kind === "blocked"
                          ? "bg-rose-50 border border-rose-200 text-rose-900"
                          : kind === "no_advice"
                            ? "bg-amber-50 border border-amber-200 text-amber-900"
                            : "bg-white border border-stone-200 text-stone-900"
                }`}>
                    {text}
                </div>
                {!isUser && sources?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {sources.map((s, i) => (
                            <Link key={i} to={s.to || "#"}
                                  data-testid="ai-source"
                                  className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-stone-200 bg-white hover:border-amber-400">
                                <span className="text-stone-500 uppercase tracking-widest text-[9px] font-bold">{s.kind}</span>
                                <span className="text-stone-800 truncate max-w-[160px]">{s.label}</span>
                                <ChevronRight size={10} className="text-stone-400" />
                            </Link>
                        ))}
                    </div>
                )}
                {!isUser && suggestions?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {suggestions.map((s, i) => (
                            s.prompt
                                ? <button key={i} onClick={() => onSuggest(s.prompt)} data-testid="ai-suggest"
                                          className="text-[11.5px] px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700">{s.label}</button>
                                : <Link key={i} to={s.to || "#"} data-testid="ai-suggest-link"
                                        className="inline-flex items-center gap-1 text-[11.5px] px-2.5 py-1 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-700">
                                    {s.label} <ArrowRight size={10} />
                                  </Link>
                        ))}
                    </div>
                )}
                {!isUser && onFeedback && (
                    <div className="mt-2 flex items-center gap-1 text-stone-400">
                        <button onClick={() => onFeedback(true)}  data-testid="ai-fb-helpful"     className="p-1 rounded hover:bg-stone-100 hover:text-emerald-700"><ThumbsUp size={11} /></button>
                        <button onClick={() => onFeedback(false)} data-testid="ai-fb-not-helpful" className="p-1 rounded hover:bg-stone-100 hover:text-rose-700"><ThumbsDown size={11} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default function VaultIntelligence() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [gate, setGate] = useState(null);   // /api/ai/status
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        api.get("/ai/status").then((r) => setGate(r.data)).catch(() => setGate({ enabled: false }));
    }, []);

    // Auto-send a prompt passed from the Concierge hero
    useEffect(() => {
        const autoAsk = location.state?.autoAsk;
        if (autoAsk && gate?.enabled) {
            send(autoAsk);
            // Clear so re-mounts don't re-send
            window.history.replaceState({}, "");
        }
    }, [gate?.enabled]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async (text) => {
        const t = (text ?? input).trim();
        if (!t || busy) return;
        setMessages((m) => [...m, { role: "user", text: t }]);
        setInput(""); setBusy(true);
        try {
            const { data } = await api.post("/ai/ask", { message: t, session_id: sessionId });
            if (data.session_id) setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", ...data }]);
        } catch (e) {
            setMessages((m) => [...m, {
                role: "assistant", kind: "unknown",
                text: e.response?.status === 403
                    ? "VAULT Intelligence is not yet available on your workspace."
                    : "VAULT Intelligence is temporarily unavailable. Your VAULT records remain available normally.",
            }]);
        } finally { setBusy(false); }
    };

    const sendFeedback = (helpful) => {
        api.post("/ai/ask/feedback", { session_id: sessionId, helpful }).catch(() => {});
    };

    const clearAll = async () => {
        await api.delete("/ai/ask/history").catch(() => {});
        setMessages([]); setSessionId(null);
    };

    if (!gate) return <div className="p-6 text-stone-500">Loading…</div>;

    // First-use welcome
    if (messages.length === 0) {
        return (
            <div className="max-w-3xl mx-auto" data-testid="ai-welcome">
                <div className="mb-8">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold uppercase tracking-widest">
                        <Sparkles size={11} /> VAULT Intelligence · AI
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none mt-4">Meet VAULT Intelligence</h1>
                    <p className="text-base text-stone-600 mt-4 max-w-2xl">
                        Ask questions about the information inside your VAULT. Find records,
                        understand upcoming obligations and stay organised faster.
                    </p>
                </div>

                {!gate.enabled && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-6 flex items-start gap-2 text-[13px] text-amber-900" data-testid="ai-not-enabled-notice">
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                        <div>
                            VAULT Intelligence is currently in a controlled rollout. It&rsquo;s not
                            yet enabled for your workspace. <Link to="/ai-disclosure" className="underline">Learn how it works →</Link>
                        </div>
                    </div>
                )}

                <div className="rounded-2xl border border-stone-200 bg-white p-6">
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Try one of these</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {SUGGESTED.map((s, i) => (
                            <button key={i} onClick={() => send(s)} disabled={!gate.enabled}
                                    data-testid={`ai-example-${i}`}
                                    className="text-left px-4 py-3 rounded-xl border border-stone-200 hover:border-amber-400 hover:bg-amber-50/40 text-[13.5px] text-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mt-8">
                    <div className="relative">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                            placeholder="Ask VAULT anything about your financial records…"
                            data-testid="ai-input"
                            disabled={!gate.enabled}
                            className="w-full pl-5 pr-14 py-4 rounded-2xl border border-stone-200 focus:border-amber-400 focus:outline-none text-[14px] disabled:opacity-50"
                        />
                        <button onClick={() => send()} disabled={busy || !gate.enabled || !input.trim()}
                                data-testid="ai-send"
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-stone-900 hover:bg-stone-800 text-white flex items-center justify-center disabled:opacity-40">
                            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        </button>
                    </div>
                </div>

                <div className="mt-6 rounded-xl border border-stone-200 bg-white p-3 flex items-start gap-2 text-[11.5px] text-stone-500">
                    <Info size={12} className="mt-0.5 shrink-0" />
                    <span>
                        VAULT Intelligence can make mistakes. Verify important information.
                        It doesn&rsquo;t provide financial, investment, tax, insurance, credit or legal advice.
                        · <Link to="/ai-disclosure" className="underline">Learn more</Link>
                    </span>
                </div>
            </div>
        );
    }

    // Chat view
    return (
        <div className="max-w-3xl mx-auto flex flex-col" style={{ minHeight: "calc(100vh - 200px)" }} data-testid="ai-chat">
            <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-bold uppercase tracking-widest">
                    <Sparkles size={11} /> VAULT Intelligence · AI
                </div>
                <button onClick={clearAll} className="inline-flex items-center gap-1 text-[11.5px] text-stone-500 hover:text-rose-700" data-testid="ai-clear">
                    <Trash2 size={11} /> Clear
                </button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto pr-2" data-testid="ai-messages">
                {messages.map((m, i) => (
                    <Message key={i} {...m}
                             onSuggest={send}
                             onFeedback={m.role === "assistant" && i === messages.length - 1 ? sendFeedback : null} />
                ))}
                {busy && (
                    <div className="flex items-center gap-2 text-stone-500 text-[12.5px]" data-testid="ai-thinking">
                        <Loader2 size={12} className="animate-spin" /> Looking through your VAULT…
                    </div>
                )}
            </div>
            <div className="pt-4 border-t border-stone-100 mt-4">
                <div className="relative">
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                        placeholder="Ask a follow-up…"
                        data-testid="ai-input-chat"
                        className="w-full pl-5 pr-14 py-3 rounded-full border border-stone-200 focus:border-amber-400 focus:outline-none text-[14px]"
                    />
                    <button onClick={() => send()} disabled={busy || !input.trim()}
                            data-testid="ai-send-chat"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-stone-900 hover:bg-stone-800 text-white flex items-center justify-center disabled:opacity-40">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    </button>
                </div>
                <p className="mt-2 text-[10.5px] text-stone-400 text-center">
                    VAULT Intelligence can make mistakes · No financial advice · <Link to="/ai-disclosure" className="underline">Learn more</Link>
                </p>
            </div>
        </div>
    );
}
